import { Button, Menu, Modal } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Grid } from 'styled-css-grid';
import { StyledMenu } from '../StyledSidebarComponent';
import {
  ColoredCell,
  ContentContainer,
  GeneralInfoContainer,
  Header,
  HomepageLink,
  LinkImg,
  StyledButton,
  StyledDivider,
  StyledIcon,
  StyledLicenseCell
} from './AboutMenuStyled';
import { AboutIcon } from './icons/AboutIcon';
import geosecmaLinkedin from './images/puff-geosecma-linkedin.png';
import geosecmaMinaSidor from './images/puff-geosecma-mina_sidor.png';
import geosecmaNyhetsbrev from './images/puff-geosecma-nyhetsbrev.png';
import geosecmaSupport from './images/puff-geosecma-support.png';

export interface ILicense {
  name: string;
  link: string;
  licenseType: string;
  comment: string;
}

export interface IAboutMenuProps {
  jsLicenses: Array<ILicense>;
  nugetLicenses: Array<ILicense>;
  grcLicenses: Array<ILicense>;
  licensesListText: string;
  title: string;
  versionNumberComponent: JSX.Element;
  showSupportLinks?: boolean;
}

export const AboutMenu = ({
  jsLicenses = [],
  nugetLicenses = [],
  grcLicenses = [],
  licensesListText = 'License text',
  title = 'GEOSECMA Licences',
  versionNumberComponent,
  showSupportLinks = true,
  ...props
}: IAboutMenuProps) => {
  const { t } = useTranslation('geosecma-sidebar');
  const [showLicenses, setShowLicenses] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  const newLicenses = [...jsLicenses, ...nugetLicenses];
  grcLicenses.forEach((license) => {
    if (!newLicenses.find((tl) => tl.name === license.name)) {
      newLicenses.push(license);
    }
  });

  const sortedLicenses = newLicenses.sort((a, b) => (a.name > b.name ? 1 : -1));

  sortedLicenses.forEach((license) => {
    if (license.link.startsWith('git+')) {
      license.link = license.link.substring(4);
    }
  });

  const showAbout = () => {
    setVisible(true);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const toggleLicenses = () => {
    setShowLicenses((c) => !c);
  };

  return (
    <>
      <StyledMenu theme="dark" selectable={false}>
        <Menu.Item
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && showAbout()}
          onClick={showAbout}
          key="1"
          {...props}
        >
          <StyledIcon component={AboutIcon} aria-label={t('about')} />
          <span>{t('about')}</span>
        </Menu.Item>
      </StyledMenu>
      <Modal width={550} footer={null} bodyStyle={{ padding: 30 }} visible={visible} onCancel={handleCancel} centered>
        <Header>{showLicenses ? licensesListText : title}</Header>
        {showLicenses ? (
          <ContentContainer>
            <Grid columns={1}>
              {sortedLicenses.map((license) => (
                <ColoredCell key={license.name} data-testid="licenseCell">
                  <Grid columns="1.7fr 1fr 0.5fr 0.5fr">
                    <Cell>{license.name}</Cell>
                    <Cell>{license.licenseType}</Cell>
                    <Cell>{license.comment}</Cell>
                    <StyledLicenseCell>
                      {license.link && (
                        <HomepageLink as="a" href={license.link} target="_blank" rel="noopener noreferrer">
                          {t('homepage')}
                        </HomepageLink>
                      )}
                    </StyledLicenseCell>
                  </Grid>
                </ColoredCell>
              ))}
            </Grid>
          </ContentContainer>
        ) : (
          <GeneralInfoContainer>
            <>{versionNumberComponent}</>
            <Button data-testid="licensesButton" onClick={toggleLicenses}>
              {t('licences')}
            </Button>
          </GeneralInfoContainer>
        )}
        <StyledDivider />
        {showLicenses ? (
          <StyledButton data-testid="backButton" onClick={toggleLicenses}>
            {t('back')}
          </StyledButton>
        ) : showSupportLinks ? (
          <Grid columns={2}>
            <Cell>
              <a href="https://easit.sgroup-solutions.se/selfservice" rel="noopener noreferrer" target="_blank">
                <LinkImg src={geosecmaMinaSidor} alt="geosecmaMinaSidor" />
              </a>
              <a href="https://sgroup-solutions.se/Support/Support" rel="noopener noreferrer" target="_blank">
                <LinkImg src={geosecmaSupport} alt="geosecmaSupport" />
              </a>
            </Cell>
            <Cell>
              <a href="https://sgroup-solutions.se/Om-oss/Nyhetsbrev" rel="noopener noreferrer" target="_blank">
                <LinkImg src={geosecmaNyhetsbrev} alt="geosecmaNyhetsbrev" />
              </a>
              <a href="https://www.linkedin.com/company/s-group-solutions-ab" rel="noopener noreferrer" target="_blank">
                <LinkImg src={geosecmaLinkedin} alt="geosecmaLinkedin" />
              </a>
            </Cell>
          </Grid>
        ) : (
          // when hidden support links, lets get the other links side by side instead of in same cell
          <Grid columns={2}>
            <Cell>
              <a href="https://sgroup-solutions.se/Om-oss/Nyhetsbrev" rel="noopener noreferrer" target="_blank">
                <LinkImg src={geosecmaNyhetsbrev} alt="geosecmaNyhetsbrev" />
              </a>
            </Cell>
            <Cell>
              <a href="https://www.linkedin.com/company/s-group-solutions-ab" rel="noopener noreferrer" target="_blank">
                <LinkImg src={geosecmaLinkedin} alt="geosecmaLinkedin" />
              </a>
            </Cell>
          </Grid>
        )}
      </Modal>
    </>
  );
};
